import React, {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'astroturf/react';
import { FeedContext } from 'js/social/contexts/feed';
import ModalBase from 'js/components/modals/Modal';
import { Banner } from 'js/design_system/Banner';
import { DeprecatedBreakpoint } from 'js/design_system/DeprecatedBreakpoint';
import { Button } from 'js/design_system/Button';
import { TextButton } from 'js/design_system/TextButton';
import { Colors } from 'js/design_system/Color';
import { Heading as HeadingBase } from 'js/design_system/Heading';
import CloseIcon from 'js/design_system/icons/close.svg';
import { ImageIcon, InfoIcon, PaperIcon } from 'js/social/shared/Icons';
import UserCard, { User as Author } from 'js/social/UserCard';
import {
  SocialEditor,
  SocialEditorHandle,
} from 'js/social/shared/SocialEditor/SocialEditor';
import {
  FollowableUser,
  ResourcePayload,
  ResourceType,
  Tag,
  Image,
  Paper,
} from 'js/social/shared/types';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import {
  getImpressionPage,
  getSocialImpressionSource,
} from 'js/social/ImpressionHelper';
import {
  AttachmentStatus,
  useAttachment,
} from 'js/social/shared/attachments/useAttachment';
import { usePaperLinkDetection } from 'js/social/shared/attachments/usePaperLinkDetection';
import useBreakpoint from 'js/utils/hooks/useDeprecatedBreakpoint';
import _Modal from "CreateSocialItem-Modal.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/CreateSocialItem/CreateSocialItem.tsx?styleId=Modal";
import _HeadingGroup from "CreateSocialItem-HeadingGroup.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/CreateSocialItem/CreateSocialItem.tsx?styleId=HeadingGroup";
import _Heading from "CreateSocialItem-Heading.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/CreateSocialItem/CreateSocialItem.tsx?styleId=Heading";
import _CloseButton from "CreateSocialItem-CloseButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/CreateSocialItem/CreateSocialItem.tsx?styleId=CloseButton";
import _ContentContainer from "CreateSocialItem-ContentContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/CreateSocialItem/CreateSocialItem.tsx?styleId=ContentContainer";
import _SocialEditorContainer from "CreateSocialItem-SocialEditorContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/CreateSocialItem/CreateSocialItem.tsx?styleId=SocialEditorContainer";
import _ButtonRow from "CreateSocialItem-ButtonRow.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/CreateSocialItem/CreateSocialItem.tsx?styleId=ButtonRow";
import _ButtonGroup from "CreateSocialItem-ButtonGroup.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/CreateSocialItem/CreateSocialItem.tsx?styleId=ButtonGroup";
import _ActionButton from "CreateSocialItem-ActionButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/CreateSocialItem/CreateSocialItem.tsx?styleId=ActionButton";
import _PrimaryButton from "CreateSocialItem-PrimaryButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/CreateSocialItem/CreateSocialItem.tsx?styleId=PrimaryButton";
import _AttachButton from "CreateSocialItem-AttachButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/CreateSocialItem/CreateSocialItem.tsx?styleId=AttachButton";
import _Divider from "CreateSocialItem-Divider.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/CreateSocialItem/CreateSocialItem.tsx?styleId=Divider";


const Modal = /*#__PURE__*/styled(ModalBase, null, {
  displayName: "Modal",
  styles: _Modal
});

const HeadingGroup = /*#__PURE__*/styled("div", null, {
  displayName: "HeadingGroup",
  styles: _HeadingGroup
});

const Heading = /*#__PURE__*/styled(HeadingBase, null, {
  displayName: "Heading",
  styles: _Heading
});

const CloseButton = /*#__PURE__*/styled(TextButton, null, {
  displayName: "CloseButton",
  styles: _CloseButton
});

const ContentContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ContentContainer",
  styles: _ContentContainer
});

const SocialEditorContainer = /*#__PURE__*/styled("div", null, {
  displayName: "SocialEditorContainer",
  styles: _SocialEditorContainer
});

const ButtonRow = /*#__PURE__*/styled("div", null, {
  displayName: "ButtonRow",
  styles: _ButtonRow
});

const ButtonGroup = /*#__PURE__*/styled("div", null, {
  displayName: "ButtonGroup",
  styles: _ButtonGroup
});

const ActionButton = /*#__PURE__*/styled(TextButton, null, {
  displayName: "ActionButton",
  styles: _ActionButton
});

const PrimaryButton = /*#__PURE__*/styled(Button, null, {
  displayName: "PrimaryButton",
  styles: _PrimaryButton
});

const AttachButton = /*#__PURE__*/styled(ActionButton, null, {
  displayName: "AttachButton",
  styles: _AttachButton
});

const Divider = /*#__PURE__*/styled("div", null, {
  displayName: "Divider",
  styles: _Divider
});

const loadDraft = (
  id: string,
  getUserData: (id: number) => FollowableUser,
): { content: string; tags: Tag[] } => {
  const draft = window.sessionStorage.getItem(id);
  if (draft != null) {
    try {
      const parsedDraft = JSON.parse(draft);
      // Attempt to get the user data for each tagged user. If it fails, just clear the draft.
      parsedDraft.tags.forEach((tag: Tag) => {
        getUserData(tag.tagId);
      });
      return parsedDraft;
    } catch {
      window.sessionStorage.removeItem(id);
    }
  }
  return { content: '', tags: [] };
};

type Props = {
  /**
   * Some form of identifier for the social item.
   * Used for saving drafts in sessionStorage.
   */
  id: string;
  /**
   * Depending on the resource type, different options will appear
   * in the modal, e.g. images.
   */
  type: ResourceType;
  /**
   * Info about the current user, used to show their
   * name, image, and affiliation.
   */
  author: Author;
  /** Copy for the modal heading */
  heading: string;
  /** Copy for the text area input */
  placeholder?: string;
  /** Copy for the primary CTA button. */
  cta: string;
  /** Anything to prefill the input box with */
  initialContent?: string;
  /** Anything to prefill the tags with */
  initialTags?: Tag[];
  /** Any image that comes pre-attached */
  initialImage?: Image;
  /** Any paper that comes pre-attached */
  initialPaper?: Paper;
  /** If an attachment is present, can it be removed? */
  attachmentRemovable?: boolean;
  /** Node to be displayed below the CTA buttons. */
  footer?: React.ReactNode;
  /** Whether or not some form of content is required for submission. */
  inputRequired?: boolean;
  /** Optionally disable tagging for this item. */
  disableTags?: boolean;
  /** Optionally disable hashtags for this item. */
  disableHashtags?: boolean;
  /** Optionally disable images for this item. */
  disableImages?: boolean;
  /** Optionally disable papers for this item. */
  disablePapers?: boolean;
  /**
   * Clicking outside the modal, clicking Cancel,
   * or clicking the close 'X' calls this function.
   */
  onClose: () => unknown;
  /** This function is called when a user submits their social item. */
  onSubmit: (payload: ResourcePayload) => unknown;
};

type ItemDraft = {
  content: string;
  tags: Tag[];
};

const CreateSocialItem: FC<React.PropsWithChildren<Props>> = ({
  id,
  type,
  author,
  heading,
  placeholder,
  cta,
  initialContent,
  initialTags,
  initialImage = undefined,
  initialPaper = undefined,
  inputRequired = true,
  disableTags = false,
  disableHashtags = false,
  disableImages = type !== 'post',
  disablePapers = type !== 'post',
  attachmentRemovable = true,
  onClose,
  onSubmit,
  footer,
  children,
}) => {
  const {
    feedConfig: { page },
    userActions: { getUserData },
  } = useContext(FeedContext);

  // Load draft
  const [startingDraftSelected, setStartingDraftSelected] = useState(false);
  let initialDraft: ItemDraft = { content: '', tags: [] };
  if (!startingDraftSelected) {
    initialDraft =
      initialContent === undefined && initialTags === undefined
        ? loadDraft(id, getUserData)
        : { content: initialContent || '', tags: initialTags || [] };
    setStartingDraftSelected(true);
  }

  const [content, setContent] = useState(initialDraft.content);
  const [tags, setTags] = useState<Tag[]>(initialDraft.tags);
  const [submitting, setSubmitting] = useState(false);
  const [prompt, setPrompt] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const socialEditorRef = useRef<SocialEditorHandle>(null);
  const mobile = useBreakpoint() < DeprecatedBreakpoint.tablet;

  const hasText = useMemo(
    () => !!(content.trim() || tags.length > 0),
    [content, tags],
  );

  // Attachments
  const {
    image,
    paper,
    content: attachmentContent,
    status: attachmentStatus,
    hasAttachment,
    onImageAttach,
    onPaperAttach,
    onPaperLinksChange,
  } = useAttachment(initialImage, initialPaper, attachmentRemovable);
  usePaperLinkDetection(
    content,
    disablePapers ? () => null : onPaperLinksChange,
  );

  // If this is a create post modal, wrap elements in ImpressionWatcher for click tracking
  const trackImpressions = page !== 'single_post' && type === ResourceType.Post;
  const IMPRESSION_PAGE = getImpressionPage(page);
  const IMPRESSION_SECTION = 'prompt';

  // Cancel prompt if the user removes the attachment
  useEffect(() => {
    if (prompt && !hasAttachment) {
      setPrompt(false);
    }
  }, [prompt, hasAttachment]);

  const { allowSubmit, loading } = useMemo(() => {
    const hasContent = !inputRequired || hasText || hasAttachment;
    const loading = attachmentStatus === AttachmentStatus.Loading || submitting;
    const allowSubmit = hasContent && !loading;
    return { allowSubmit, loading };
  }, [inputRequired, submitting, attachmentStatus, hasAttachment, hasText]);

  const submit = async () => {
    if (!allowSubmit) return;

    if (!prompt && !hasText && hasAttachment) {
      socialEditorRef.current?.focus();
      return setPrompt(true);
    }

    setSubmitting(true);
    try {
      await onSubmit({ content, tags, image, paper });
      window.sessionStorage.removeItem(id);
      onClose();
    } catch {
      setSubmitting(false);
    }
  };

  const saveDraft = useCallback(() => {
    window.sessionStorage.setItem(id, JSON.stringify({ content, tags }));
  }, [content, tags, id]);

  const closeModal = useCallback(() => {
    if (!loading && initialContent === undefined) {
      saveDraft();
      onClose();
    }
  }, [loading, onClose, initialContent, saveDraft]);

  const closeModalOnEscape = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    },
    [closeModal],
  );

  const handleAddImage = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.currentTarget.files?.length) {
        onImageAttach(event.currentTarget.files[0]);
      }
    },
    [onImageAttach],
  );

  const ctas = (
    <>
      <ImpressionWatcher
        active={trackImpressions}
        source={getSocialImpressionSource(
          IMPRESSION_PAGE,
          IMPRESSION_SECTION,
          'cancel',
        )}
      >
        <ActionButton onClick={onClose} disabled={loading}>
          Cancel
        </ActionButton>
      </ImpressionWatcher>
      <ImpressionWatcher
        active={trackImpressions}
        source={getSocialImpressionSource(
          IMPRESSION_PAGE,
          IMPRESSION_SECTION,
          'post',
        )}
      >
        <PrimaryButton disabled={!allowSubmit} onClick={submit}>
          {cta}
        </PrimaryButton>
      </ImpressionWatcher>
    </>
  );

  const wrappedFooter = footer ? (
    <ImpressionWatcher
      active={trackImpressions}
      source={getSocialImpressionSource(
        IMPRESSION_PAGE,
        IMPRESSION_SECTION,
        'info',
      )}
    >
      {footer}
    </ImpressionWatcher>
  ) : null;

  const textPrompt = (
    <ImpressionWatcher
      active={trackImpressions}
      source={getSocialImpressionSource(
        IMPRESSION_PAGE,
        IMPRESSION_SECTION,
        'add_text_prompt',
      )}
    >
      <Banner type="warning" icon={<InfoIcon />}>
        {image
          ? 'Write something about this image to help those who rely on text.'
          : 'Write something to explain why you are sharing this paper.'}
      </Banner>
    </ImpressionWatcher>
  );

  return (
    <Modal
      containerProps={{
        onKeyDown: closeModalOnEscape,
      }}
      onClose={closeModal}
    >
      <HeadingGroup>
        {mobile ? (
          ctas
        ) : (
          <>
            <Heading size="sm">{heading}</Heading>
            <CloseButton
              aria-label="Close"
              onClick={onClose}
              disabled={loading}
            >
              <CloseIcon color={Colors.GrayDark} />
            </CloseButton>
          </>
        )}
      </HeadingGroup>

      <Divider />

      <ContentContainer>
        <UserCard {...author} />

        {prompt && textPrompt}

        <ImpressionWatcher
          active={trackImpressions}
          source={getSocialImpressionSource(
            IMPRESSION_PAGE,
            IMPRESSION_SECTION,
            'text',
          )}
        >
          <SocialEditorContainer>
            <SocialEditor
              ref={socialEditorRef}
              content={content}
              tags={tags}
              placeholder={placeholder}
              disableTags={disableTags}
              disableHashtags={disableHashtags}
              onChange={({ content, tags }) => {
                setContent(content);
                setTags(tags);
              }}
              onSubmit={submit}
            />
          </SocialEditorContainer>
        </ImpressionWatcher>

        <ImpressionWatcher
          active={trackImpressions}
          source={getSocialImpressionSource(
            IMPRESSION_PAGE,
            IMPRESSION_SECTION,
            'attachment',
          )}
        >
          {attachmentContent}
        </ImpressionWatcher>

        {children}
      </ContentContainer>

      {mobile && (!disableImages || !disablePapers || wrappedFooter) && (
        <Divider />
      )}

      <ButtonRow>
        <ButtonGroup>
          {!disableImages && (
            <>
              <AttachButton
                aria-label="Image"
                disabled={
                  submitting || attachmentStatus !== AttachmentStatus.Empty
                }
                icon={<ImageIcon />}
                onClick={() => imageInputRef.current?.click()}
              >
                {!mobile && 'Image'}
              </AttachButton>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAddImage}
              />
            </>
          )}
          {!disablePapers && (
            <AttachButton
              aria-label="Paper"
              disabled={
                submitting || attachmentStatus !== AttachmentStatus.Empty
              }
              icon={<PaperIcon />}
              onClick={() => onPaperAttach()}
            >
              {!mobile && 'Paper'}
            </AttachButton>
          )}
        </ButtonGroup>

        {mobile ? wrappedFooter : <ButtonGroup>{ctas}</ButtonGroup>}
      </ButtonRow>

      {!mobile && wrappedFooter && (
        <>
          <Divider />
          {wrappedFooter}
        </>
      )}
    </Modal>
  );
};

export default CreateSocialItem;
